import { LocationOnOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import countriesData from "../../data/countriesList.json";
import { useModals } from "../../context/ModalContext";
import { formatUSPhone, getRecordEmail } from "../../utils";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { SnackbarAlert } from "../common/SnackbarAlert";
import { colors, alphaVar } from "../ui/colors";
import { useDialog } from "../ui/dialogs/useDialog";
import { SASMenuStyles } from "../ui/styles/inputStyles";
import { ClientDetailsTabs } from "./ClientDetailsModal/ClientDetailsTabs";

const GRAY = colors.gray;
const ORANGE = colors.orange;

/**
 * ClientsRow component - Renders a single client row in the clients table
 *
 * @param {object} row - The client data object to display
 * @param {boolean} disableInnerChilds - If true, disables the expansion of coverage areas within the row
 * @param {boolean} allowSelected - If true, allows the row to be selected and highlights it when selected
 * @param {boolean} disableMenuOptions - If true, hides the actions menu button for this row
 * @param {boolean} disableRecordClickable - If true, prevents opening the client details modal when clicking on the row.
 *                                          The row will still expand if it has coverage areas, but won't trigger the modal.
 *                                          Also disables the pointer cursor and hover effects.
 * @param {function} handleDelete - Callback function to handle client deletion
 * @param {object|string|null} selectedClient - The currently selected client object, UID string, or null
 * @param {function} handleUpdate - Callback function to handle client updates
 * @param {function} onSelectClientAndCoverage - Callback function called when a client and coverage area are selected
 * @param {function} handleUpdateCoverageArea - Callback function to handle coverage area updates
 * @param {function} onOpenModalDetails - Callback function to open the client details modal
 */

export const ClientsRow = ({
  row,
  disableInnerChilds = false,
  allowSelected = false,
  disableMenuOptions = false,
  disableRecordClickable = false,
  handleDelete,
  selectedClient = null,
  handleUpdate,
  onSelectClientAndCoverage,
  handleUpdateCoverageArea,
  onOpenModalDetails,
  contact = null,
  linkContactToClient,
  activeTable,
}) => {
  const dispatch = useDispatch();
  const jobState = useSelector((s) => s.jobState);
  const queryClient = useQueryClient();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const { showDialog, DialogComponent } = useDialog();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const coverageTriggerRef = useRef(null);
  const updateTriggerRef = useRef(null);
  const linkContactTriggerRef = useRef(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState(null);

  const openMenu = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  const { companies } = useSelector((s) => s.userState);
  const companyId = companies?.[0]?.id;

  const getTypeColor = (type) => {
    if (type === "commercial") return colors.orange[400];
    if (type === "residencial") return colors.blue[500];
    return theme.palette.grey[400];
  };

  const contactName = `${row.firstName || ""} ${row.lastName || ""}`.trim();
  const clientName = row.type === "commercial" ? row.clientCompanyName || contactName || "Not Provided" : contactName || "Not Provided";

  const defaultEmail = getRecordEmail(row) || "Not Provided";

  const defaultPhone =
    Array.isArray(row?.phones) && row?.phones.length > 0
      ? row?.phones.find((p) => p.isDefault) || row?.phones[0]
      : row?.phoneNumbers && row?.phoneNumbers.length > 0
        ? row?.phoneNumbers.find((p) => p.isDefault || p.isPrimary) || row?.phoneNumbers[0]
        : null;

  const phoneValue = defaultPhone?.phone || defaultPhone?.number || row?.phone || "";
  const flagCountryCode =
    defaultPhone?.countryCode ||
    (defaultPhone?.dialCode ? countriesData.find((country) => country.dialCode === defaultPhone.dialCode)?.code || "US" : "US") ||
    "US";

  const clientConcatenatedAddress =
    `${row.address || ""}${row.city ? ", " + row.city : ""}${row.state ? ", " + row.state : ""}${row.zipCode ? " " + row.zipCode : ""}`.trim();

  const canExpand = !disableInnerChilds;
  const canDelete = row?.deletable !== false;

  const handleRowClick = (e) => {
    if (e.target.closest('[data-ignore-row-click="true"]')) return;

    // Auto-expand when clicking on row if it has coverage areas
    if (!disableInnerChilds && canExpand && !open) {
      setOpen(true);
    }

    // Only open modal if record clickable is not disabled
    if (!disableRecordClickable) {
      onOpenModalDetails?.(row);
    }
  };

  // Handle both cases: selectedClient as object or as string (UID)
  const selectedClientUid = typeof selectedClient === "string" ? selectedClient : selectedClient?.uid;

  const isSelectedClient = allowSelected && selectedClientUid === row.uid;
  const hasSelectedCoverage = isSelectedClient && selectedClient && typeof selectedClient === "object" && !!selectedClient?.coverageArea?.id;

  // Auto-expand cuando este row es el seleccionado (y tiene coverage o cuando allowSelected está activo)
  useEffect(() => {
    if (!disableInnerChilds && canExpand) {
      // Expand if client is selected (with or without coverage) when allowSelected is active
      if (allowSelected && isSelectedClient) {
        setOpen(true);
      }
      // Also expand if there's a selected coverage
      if (hasSelectedCoverage) {
        setOpen(true);
      }
    }
  }, [disableInnerChilds, canExpand, hasSelectedCoverage, allowSelected, isSelectedClient, row.uid, selectedClientUid]);

  const { closeByKey } = useModals();

  const triggerSnackbar = (message, type = "info") => {
    setSnackMessage({ message, type });
    setSnackOpen(true);
  };

  const handleCoverageAreaSelect = (area) => {
    const selectedPayload = {
      ...row,
      coverageArea: area,
    };

    // console.log("Coverage area selected", {
    //   clientUid: row?.uid,
    //   clientName,
    //   coverageAreaId: area?.id,
    //   coverageAreaAddress: area?.address,
    //   coverageAreaCity: area?.city,
    //   coverageAreaState: area?.state,
    //   coverageAreaCountry: area?.country,
    //   isMain: area?.isMain,
    //   payload: selectedPayload,
    // });

    onSelectClientAndCoverage(selectedPayload);
  };

  const handleLinkContact = () => {
    if (!contact || !contact.phoneNumbers || contact.phoneNumbers.length === 0) {
      triggerSnackbar("This contact has no phone numbers to link", "error");
      return;
    }

    linkContactTriggerRef.current?.click();
  };

  return (
    <>
      <TableRow
        sx={{
          "& > *": {
            borderBottom: `1px solid ${GRAY[100]}`,
          },
          cursor: disableRecordClickable ? "default" : "pointer",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          borderLeft: "3px solid transparent",
          transition: "background-color 140ms ease, border-color 140ms ease",
          "&:hover": disableRecordClickable
            ? {}
            : {
                backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                borderLeftColor: ORANGE[200],
              },
          "&.Mui-selected": {
            backgroundColor: alphaVar(ORANGE.base, 0.06),
            borderLeftColor: ORANGE.base,
            "&:hover": {
              backgroundColor: alphaVar(ORANGE.base, 0.08),
            },
          },
        }}
        selected={isSelectedClient}
        onClick={handleRowClick}
      >
        {/* Celda Toggle*/}
        <TableCell
          sx={{
            width: isMobile ? 40 : 52,
            p: 0,
            pl: isMobile ? 1 : 2,
          }}
        >
          {!disableInnerChilds && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              sx={{ p: isMobile ? 0 : "8px" }}
            >
              <KeyboardArrowDownIcon sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
            </IconButton>
          )}
        </TableCell>

        {/* Date*/}
        {!isMobile && (
          <TableCell sx={{ minWidth: 70 }}>
            <Typography fontSize="0.875rem" sx={{ color: GRAY[700] }}>
              {dayjs(row.createdAt).format("MM/DD/YYYY")}
            </Typography>
          </TableCell>
        )}

        {/* Client */}
        <TableCell
          sx={{
            pl: isMobile ? 0 : 2,
          }}
        >
          <Typography fontSize="0.875rem" sx={{ fontWeight: 500, color: GRAY[800], lineHeight: 1.2 }}>
            {clientName}
          </Typography>
        </TableCell>

        <TableCell sx={{ minWidth: 120 }}>
          <Chip
            label={row?.type === "commercial" ? "Commercial" : row?.type === "residencial" ? "Residential" : row?.type || "Not Provided"}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.75rem",
              fontWeight: 500,
              backgroundColor: alphaVar(getTypeColor(row?.type), 0.1),
              color: getTypeColor(row?.type),
              textTransform: "capitalize",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </TableCell>

        {/* Email */}
        <TableCell
          sx={{
            width: isMobile ? "100%" : 170,
            flex: isMobile ? "1 1 100%" : "none",

            px: isMobile ? 1 : 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              fontSize="0.875rem"
              sx={{
                color: GRAY[600],
                wordBreak: "break-all",
                whiteSpace: "normal",
                lineHeight: 1.2,
              }}
            >
              {defaultEmail ? defaultEmail : "Not Provided"}
            </Typography>
          </Box>
        </TableCell>

        {/* Phone */}
        <TableCell sx={{ display: isMobile ? "none" : "table-cell", minWidth: 180 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {defaultPhone && <ReactCountryFlag countryCode={flagCountryCode} svg style={{ width: "20px", height: "20px", borderRadius: "4px" }} />}
            <Typography fontSize="0.875rem" sx={{ color: GRAY[600] }}>
              {phoneValue ? formatUSPhone(phoneValue) : "Not Provided"}
            </Typography>
          </Box>
        </TableCell>

        {contact && (
          <TableCell align="right" sx={{ width: 100 }} data-ignore-row-click={true} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="contained"
              size="small"
              onClick={handleLinkContact}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                bgcolor: ORANGE.base,
                color: "#fff",
                "&:hover": {
                  bgcolor: ORANGE[600],
                },
              }}
            >
              Select
            </Button>
          </TableCell>
        )}

        {activeTable === "prospects" && (
          <TableCell align="right" sx={{ width: 150 }} data-ignore-row-click={true} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  await handleUpdate(row.uid, { status: "ACTIVE" });
                  triggerSnackbar("Converted to active client successfully", "success");
                } catch (err) {
                  triggerSnackbar(err.message || "Failed to convert client", "error");
                }
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: ORANGE.base,
                color: ORANGE.base,
                "&:hover": {
                  bgcolor: alphaVar(ORANGE.base, 0.08),
                  borderColor: ORANGE[600],
                },
              }}
            >
              Convert to Client
            </Button>
          </TableCell>
        )}

        {/* Actions */}
        {!disableMenuOptions && (
          <TableCell
            align="right"
            sx={{ width: 64 }}
            data-ignore-row-click={true}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {canDelete ? (
              <>
                <IconButton
                  size="small"
                  aria-controls={menuOpen ? "menu-options" : undefined}
                  aria-haspopup="true"
                  data-ignore-row-click={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    openMenu(e);
                  }}
                  sx={{
                    color: colors.orange.base,
                    "&:hover": {
                      backgroundColor: alphaVar(colors.orange.base, 0.08),
                    },
                  }}
                >
                  <MoreHorizIcon />
                </IconButton>

                <Menu id="menu-options" anchorEl={menuAnchor} open={menuOpen} onClose={closeMenu} sx={SASMenuStyles}>
                  <MenuItem
                    onClick={() => {
                      updateTriggerRef.current?.click();
                      closeMenu();
                    }}
                    data-ignore-row-click={true}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Update</ListItemText>
                  </MenuItem>

                  <MenuItem
                    disabled={!canDelete}
                    data-ignore-row-click={true}
                    onClick={() => {
                      showDialog({
                        type: "warning",
                        title: "Delete Client",
                        message: `Are you sure you want to delete "${clientName}"? This action cannot be undone.`,
                        onConfirm: () => {
                          setTimeout(() => {
                            handleDelete(row.uid);
                          }, 600);
                          triggerSnackbar("Client deleted successfully", "success");
                        },
                      });
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                  </MenuItem>
                </Menu>

                <BaseModalComponent
                  modalKey={`update-client-${row.uid}`}
                  modalLabel={`Update ${clientName}`}
                  elementTrigger={
                    <button
                      ref={updateTriggerRef}
                      type="button"
                      style={{ position: "absolute", left: -99999, top: -99999 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                >
                  <ClientDetailsTabs client={row} />
                </BaseModalComponent>
              </>
            ) : (
              <IconButton data-ignore-row-click={true} size="small" disabled sx={{ color: GRAY[300] }}>
                <MoreHorizIcon />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>

      {!disableInnerChilds && (
        <TableRow>
          <TableCell
            colSpan={isMobile ? 5 : 7}
            sx={{
              p: 0,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              borderBottom: open ? `1px solid ${GRAY[100]}` : "none",
            }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 18,
                      borderRadius: 2,
                      backgroundColor: ORANGE.base,
                    }}
                  />

                  <Typography fontSize="0.875rem" sx={{ fontWeight: 600, color: GRAY[800] }}>
                    Coverage Areas
                  </Typography>
                </Box>

                {!row?.coverageAreas || row.coverageAreas.length === 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography fontSize="0.875rem" sx={{ color: GRAY[500], fontStyle: "italic", mb: 0.5 }}>
                      No coverage areas registered.
                    </Typography>

                    {clientConcatenatedAddress && (
                      <Typography fontSize="0.8125rem" sx={{ color: ORANGE.base, fontWeight: 500 }}>
                        Detected address: {clientConcatenatedAddress}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {row.coverageAreas?.map((area) => {
                      const isSelected = allowSelected && selectedClient?.uid === row.uid && selectedClient?.coverageArea?.id === area.id;

                      return (
                        <Box
                          key={area.id}
                          onClick={allowSelected ? () => handleCoverageAreaSelect(area) : undefined}
                          sx={{
                            display: "flex",
                            alignItems: isMobile ? "flex-start" : "center",
                            justifyContent: "space-between",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? 1.5 : 3,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: isSelected ? alphaVar(ORANGE.base, 0.05) : "var(--fx-surface-base, #FFFFFF)",
                            border: `1px solid ${isSelected ? ORANGE.base : GRAY[100]}`,
                            boxShadow: "none",
                            cursor: allowSelected ? "pointer" : "default",
                            transition: "background-color 140ms ease, border-color 140ms ease",

                            "&:hover": allowSelected
                              ? {
                                  borderColor: isSelected ? ORANGE.base : alphaVar(ORANGE.base, 0.45),
                                  backgroundColor: isSelected ? alphaVar(ORANGE.base, 0.06) : alphaVar(ORANGE.base, 0.025),
                                  boxShadow: "none",
                                }
                              : undefined,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: alphaVar(ORANGE.base, 0.08),
                                color: ORANGE.base,
                                flexShrink: 0,
                              }}
                            >
                              <LocationOnOutlined sx={{ fontSize: 20 }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                fontSize="0.9375rem"
                                sx={{
                                  fontWeight: 700,
                                  color: GRAY[900],
                                  lineHeight: 1.3,
                                  wordBreak: "break-word",
                                }}
                              >
                                {area.address}
                              </Typography>

                              <Typography
                                fontSize="0.8125rem"
                                sx={{
                                  color: GRAY[500],
                                  mt: 0.35,
                                }}
                              >
                                {area.isMain ? "Main Service Location" : "Service Location"}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(3, minmax(90px, auto))",
                              gap: isMobile ? 1.5 : 3,
                              width: isMobile ? "100%" : "auto",
                              pl: isMobile ? 0 : 3,
                              borderLeft: isMobile ? "none" : `1px solid ${alphaVar(GRAY[200], 0.8)}`,
                            }}
                          >
                            <CoverageInfo label="City" value={area.city} />
                            <CoverageInfo label="State" value={area.state} />
                            <CoverageInfo label="Country" value={area.country} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <SnackbarAlert
        openState={snackOpen}
        setOpenState={setSnackOpen}
        severity={snackMessage?.type}
        message={snackMessage?.message}
        position="bottom-left"
        scope="local"
      />

      {DialogComponent}

      {contact && (
        <BaseModalComponent
          modalKey={`link-contact-${row.uid}`}
          modalLabel="Link Phone Number"
          defaultSizes={{ width: 450, height: "auto" }}
          elementTrigger={
            <button
              ref={linkContactTriggerRef}
              type="button"
              style={{ position: "absolute", left: -99999, top: -99999 }}
              onClick={(e) => e.stopPropagation()}
            />
          }
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography fontSize="0.875rem" sx={{ color: GRAY[700], lineHeight: 1.5 }}>
              Are you sure you want to add phone number{" "}
              <strong>{contact?.phoneNumbers?.[0]?.number ? formatUSPhone(contact.phoneNumbers[0].number) : ""}</strong> to client{" "}
              <strong>{clientName}</strong>?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => closeByKey(`link-contact-${row.uid}`)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: GRAY[300],
                  color: GRAY[700],
                  "&:hover": {
                    borderColor: GRAY[400],
                    backgroundColor: alphaVar(GRAY[400], 0.05),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={async () => {
                  try {
                    const res = await linkContactToClient(row, contact);

                    if (res?.message) {
                      triggerSnackbar(res.message, "info");
                    } else {
                      triggerSnackbar("Phone number linked to client successfully", "success");
                    }
                  } catch (error) {
                    triggerSnackbar(error.message || "Failed to link phone number", "error");
                  } finally {
                    closeByKey(`link-contact-${row.uid}`);
                  }
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: ORANGE.base,
                  color: "#fff",
                  "&:hover": {
                    bgcolor: ORANGE[600],
                  },
                }}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </BaseModalComponent>
      )}
    </>
  );
};

const CoverageInfo = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      fontSize="0.625rem"
      sx={{
        color: ORANGE.base,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        mb: 0.35,
      }}
    >
      {label}
    </Typography>

    <Typography
      fontSize="0.875rem"
      sx={{
        color: GRAY[800],
        fontWeight: 600,
        lineHeight: 1.3,
        wordBreak: "break-word",
      }}
    >
      {value || "N/A"}
    </Typography>
  </Box>
);
